/**
 * DeskOverviewPage — the "Bloomberg Launchpad" for getbitsy.
 * Six operator desks summarised on one screen, auto-polling every 30s.
 * Designed to be projected on a wall monitor during morning briefings.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  Activity, Eye, Wallet, Lock, Vote, Shield, ArrowRight, RefreshCw, Layers, GripVertical, RotateCcw,
  Bookmark, Save, Share2, Check, Trash2, Pencil, ChevronDown, Plus, CircleDot,
} from 'lucide-react';
import { toast } from 'sonner';
import { BACKEND_URL } from '@/lib/backendUrl';
import TerminalFloorNav from '@/components/TerminalFloorNav';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import {
  listLayouts,
  getActiveLayoutId,
  setActiveLayoutId,
  saveLayout,
  updateLayout,
  deleteLayout,
  buildShareUrl,
  parseSharedLayout,
} from '@/lib/launchpadLayouts';

const DESKS = [
  { key: 'marina',     path: '/marina',         label: 'Marina',     sub: 'operations',  icon: Activity, code: 'OPS' },
  { key: 'argus',      path: '/argus',          label: 'Argus',      sub: 'audit',       icon: Eye,      code: 'AUD' },
  { key: 'treasury',   path: '/treasury',       label: 'Treasury',   sub: 'capital',     icon: Wallet,   code: 'CAP' },
  { key: 'staking',    path: '/staking',        label: 'Staking',    sub: 'yield',       icon: Lock,     code: 'STK' },
  { key: 'governance', path: '/governance',     label: 'Governance', sub: 'votes',       icon: Vote,     code: 'GOV' },
  { key: 'platform',   path: '/platform-admin', label: 'Platform',   sub: 'root',        icon: Shield,   code: 'ROOT' },
];
const DEFAULT_ORDER = DESKS.map((d) => d.key);
const DESK_BY_KEY = Object.fromEntries(DESKS.map((d) => [d.key, d]));

/* ─────────────────────────────── LIVE CLOCK ─────────────────────────────── */
function LiveClock() {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const iv = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(iv);
  }, []);
  return (
    <span className="font-mono text-[11px] text-[color:var(--t-text-2)] tabular-nums" data-testid="overview-clock">
      {now.toISOString().replace('T', ' ').slice(0, 19)} UTC
    </span>
  );
}

/* ─────────────────────────────── DESK PANEL ─────────────────────────────── */
function DeskPanel({ desk, data, flashKey, draggedKey, dropTargetKey, onDragStart, onDragEnd, onDragOver, onDrop }) {
  const Icon = desk.icon;
  const attention = data?.attention;
  const isAlert = attention?.level === 'alert';
  const isWarn = attention?.level === 'warn';
  const isDragging = draggedKey === desk.key;
  const isDropTarget = dropTargetKey === desk.key && draggedKey !== desk.key;

  const style = {
    opacity: isDragging ? 0.4 : 1,
    transition: 'opacity 140ms linear, border-color 160ms linear, box-shadow 160ms linear',
    ...(isDropTarget
      ? { boxShadow: 'inset 0 0 0 2px var(--t-amber), 0 0 0 4px rgba(255,176,0,0.08)' }
      : isAlert
        ? { boxShadow: 'inset 0 0 32px rgba(255,176,0,0.08)', borderColor: 'rgba(255,176,0,0.35)' }
        : isWarn
          ? { boxShadow: 'inset 0 0 20px rgba(255,176,0,0.04)' }
          : undefined),
  };

  return (
    <div
      className="t-pane flex flex-col relative"
      style={style}
      data-testid={`overview-panel-${desk.key}`}
      draggable
      onDragStart={(e) => { e.dataTransfer.effectAllowed = 'move'; onDragStart(desk.key); }}
      onDragEnd={onDragEnd}
      onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; onDragOver(desk.key); }}
      onDrop={(e) => { e.preventDefault(); onDrop(desk.key); }}
    >
      <div className="t-pane-header cursor-grab active:cursor-grabbing select-none">
        <span className="flex items-center gap-2">
          <GripVertical className="w-3 h-3 text-[color:var(--t-text-3)] opacity-60" />
          <Icon className="w-3 h-3 text-[color:var(--t-amber)]" />
          {desk.code} &middot; {desk.sub}
        </span>
        <span className="flex items-center gap-1.5">
          {isAlert ? (
            <span className="font-mono text-[9px] text-[color:var(--t-amber)] tracking-widest inline-flex items-center gap-1">
              <span className="w-1.5 h-1.5 rounded-full bg-[color:var(--t-amber)] animate-pulse" />
              HOT
            </span>
          ) : (
            <span className="flex items-center gap-1">
              <span className="t-dot-live" />
              <span className="text-[color:var(--t-emerald)] text-[9px] tracking-widest">LIVE</span>
            </span>
          )}
        </span>
      </div>

      <div className="t-pane-body flex-1 flex flex-col">
        {/* Desk name */}
        <h3 className="terminal-font font-black text-2xl tracking-tight leading-none">{desk.label}</h3>

        {/* Headline metric */}
        <div className="mt-5 flex items-baseline gap-2">
          <span
            key={`${desk.key}-${flashKey}`}
            className="num-flip terminal-font font-black text-4xl text-[color:var(--t-amber)] tabular-nums leading-none"
            data-testid={`overview-metric-${desk.key}`}
          >
            {data?.label ?? '···'}
          </span>
        </div>
        <div className="mt-1 font-mono text-[10px] text-[color:var(--t-text-3)] uppercase tracking-widest">
          {data?.unit ?? '—'}
        </div>

        {/* Advisory */}
        {attention && (
          <div
            className={
              'mt-3 font-mono text-[10px] tracking-wide flex items-center gap-1.5 ' +
              (isAlert ? 'text-[color:var(--t-amber)]' : 'text-[color:var(--t-amber)]/70')
            }
            data-testid={`overview-advisory-${desk.key}`}
          >
            <span className={isAlert ? 'w-1 h-1 rounded-full bg-[color:var(--t-amber)] animate-pulse' : 'w-1 h-1 rounded-full bg-[color:var(--t-amber)]/60'} />
            {attention.message}
          </div>
        )}

        {/* Sub-metrics */}
        <div className="mt-6 grid grid-cols-3 gap-px bg-[color:var(--t-border)] border border-[color:var(--t-border)]">
          {(data?.sub || [{ l: '—', v: '—' }, { l: '—', v: '—' }, { l: '—', v: '—' }]).map((s, i) => (
            <div key={i} className="bg-[color:var(--t-bg-2)] px-2.5 py-2.5">
              <div className="font-mono text-[9px] uppercase tracking-[0.18em] text-[color:var(--t-text-3)] truncate">{s.l}</div>
              <div className="terminal-font font-bold text-[15px] tabular-nums mt-1 text-[color:var(--t-text)] truncate">{s.v}</div>
            </div>
          ))}
        </div>

        {/* Footer: open desk */}
        <div className="mt-auto pt-5">
          <Link
            to={desk.path}
            data-testid={`overview-open-${desk.key}`}
            className="font-mono text-[11px] text-[color:var(--t-text-2)] hover:text-[color:var(--t-amber)] transition-colors inline-flex items-center gap-1.5 group"
          >
            open_{desk.code.toLowerCase()}()
            <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" />
          </Link>
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────────── PAGE ─────────────────────────────── */
export default function DeskOverviewPage() {
  const [data, setData] = useState(null);
  const [flashKey, setFlashKey] = useState(null);
  const [lastFetch, setLastFetch] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const ivRef = useRef(null);

  // Panel order — drag-reorderable, persisted to localStorage
  const location = useLocation();
  const navigate = useNavigate();

  // Inbound shared layout takes priority on mount
  const initialShared = useMemo(
    () => parseSharedLayout(location.search, DEFAULT_ORDER),
    [location.search],
  );

  const [order, setOrder] = useState(() => {
    if (initialShared) return initialShared.order;
    try {
      const saved = JSON.parse(localStorage.getItem('launchpad_order') || 'null');
      if (Array.isArray(saved) && saved.length === DEFAULT_ORDER.length && saved.every((k) => DEFAULT_ORDER.includes(k))) {
        return saved;
      }
    } catch (_e) { /* ignore */ }
    return DEFAULT_ORDER;
  });
  const [draggedKey, setDraggedKey] = useState(null);
  const [dropTargetKey, setDropTargetKey] = useState(null);

  // ─── Named layouts ───
  const [layouts, setLayouts] = useState(() => listLayouts());
  const [activeLayoutId, setActiveLayoutIdState] = useState(() => {
    // If we came in via a share link, we're viewing a "shared" pseudo-layout, not a saved one
    return initialShared ? null : getActiveLayoutId();
  });
  const [sharedLayoutName, setSharedLayoutName] = useState(initialShared?.name || null);

  const activeLayout = layouts.find((l) => l.id === activeLayoutId) || null;
  const isSharedView = !!sharedLayoutName && !activeLayoutId;

  // "unsaved changes" = active layout exists but order drifts from it
  const hasUnsavedChanges =
    !!activeLayout && activeLayout.order.join(',') !== order.join(',');

  const applyActive = (id) => {
    setActiveLayoutIdState(id);
    setActiveLayoutId(id);
  };

  const persistOrder = (next) => {
    setOrder(next);
    try { localStorage.setItem('launchpad_order', JSON.stringify(next)); }
    catch (_e) { /* ignore */ }
  };

  // Strip share params from URL once user interacts (keeps history clean)
  const dropShareParams = () => {
    if (location.search) navigate('/desk/overview', { replace: true });
    setSharedLayoutName(null);
  };

  const handleApplyLayout = (id) => {
    const l = layouts.find((x) => x.id === id);
    if (!l) return;
    dropShareParams();
    persistOrder(l.order);
    applyActive(id);
    toast.success(`Layout loaded · ${l.name}`, { duration: 1800 });
  };

  const handleSaveAs = () => {
    const suggested = sharedLayoutName || '';
    const name = window.prompt('Name this layout (e.g. "Morning ops")', suggested);
    if (!name || !name.trim()) return;
    try {
      const created = saveLayout(name.trim(), order, DEFAULT_ORDER);
      setLayouts(listLayouts());
      applyActive(created.id);
      dropShareParams();
      toast.success(`Saved · ${created.name}`, { duration: 1800 });
    } catch (e) {
      toast.error(e.message || 'Could not save layout');
    }
  };

  const handleSaveChanges = () => {
    if (!activeLayout) return;
    try {
      updateLayout(activeLayout.id, { order }, DEFAULT_ORDER);
      setLayouts(listLayouts());
      toast.success(`Updated · ${activeLayout.name}`, { duration: 1800 });
    } catch (e) {
      toast.error(e.message || 'Could not update layout');
    }
  };

  const handleRename = (layout) => {
    const next = window.prompt('Rename layout', layout.name);
    if (!next || !next.trim() || next.trim() === layout.name) return;
    try {
      updateLayout(layout.id, { name: next.trim() }, DEFAULT_ORDER);
      setLayouts(listLayouts());
      toast.success('Renamed', { duration: 1400 });
    } catch (e) {
      toast.error(e.message || 'Could not rename');
    }
  };

  const handleDelete = (layout) => {
    if (!window.confirm(`Delete "${layout.name}"? This cannot be undone.`)) return;
    deleteLayout(layout.id);
    setLayouts(listLayouts());
    if (activeLayoutId === layout.id) applyActive(null);
    toast.success('Deleted', { duration: 1400 });
  };

  const handleShare = async () => {
    const name = activeLayout?.name || sharedLayoutName || null;
    const url = buildShareUrl(order, name);
    try {
      await navigator.clipboard.writeText(url);
      toast.success('Share link copied to clipboard', {
        description: name ? `Layout · ${name}` : 'Custom layout',
        duration: 2200,
      });
    } catch (_e) {
      // Fallback — show the URL so the user can copy manually
      window.prompt('Copy this shareable link', url);
    }
  };

  const handleDragStart = (key) => setDraggedKey(key);
  const handleDragEnd = () => { setDraggedKey(null); setDropTargetKey(null); };
  const handleDragOver = (overKey) => { if (overKey !== dropTargetKey) setDropTargetKey(overKey); };
  const handleDrop = (dropKey) => {
    if (!draggedKey || draggedKey === dropKey) { handleDragEnd(); return; }
    const next = [...order];
    const from = next.indexOf(draggedKey);
    const to = next.indexOf(dropKey);
    if (from < 0 || to < 0) { handleDragEnd(); return; }
    next.splice(from, 1);
    next.splice(to, 0, draggedKey);
    persistOrder(next);
    handleDragEnd();
  };

  const resetOrder = () => {
    persistOrder(DEFAULT_ORDER);
    applyActive(null);
    dropShareParams();
  };
  const isCustomOrder = order.join(',') !== DEFAULT_ORDER.join(',');

  const load = async () => {
    setRefreshing(true);
    try {
      const r = await fetch(`${BACKEND_URL}/api/floor-nav/overview`);
      if (r.ok) {
        const d = await r.json();
        setData(d);
        setFlashKey(d.ts);
        setLastFetch(new Date());
      }
    } catch (_e) {
      /* ignore */
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => {
    load();
    ivRef.current = setInterval(load, 30000);
    return () => clearInterval(ivRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const desks = data?.desks || {};
  const alertCount = Object.values(desks).filter((d) => d?.attention?.level === 'alert').length;
  const warnCount = Object.values(desks).filter((d) => d?.attention?.level === 'warn').length;

  return (
    <div className="terminal-theme min-h-screen" data-testid="desk-overview-page">
      <div className="absolute inset-0 terminal-grid-bg opacity-40 pointer-events-none" />

      {/* Top bar */}
      <header className="sticky top-0 z-30 border-b border-[color:var(--t-border)] bg-[color:var(--t-bg)]/92 backdrop-blur">
        <div className="max-w-[1600px] mx-auto px-5 sm:px-8 lg:px-10 h-16 flex items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-2.5 shrink-0">
            <div className="w-7 h-7 border border-[color:var(--t-amber)] flex items-center justify-center">
              <span className="terminal-font text-[color:var(--t-amber)] font-black text-sm">B</span>
            </div>
            <div className="flex items-center gap-2">
              <Layers className="w-3.5 h-3.5 text-[color:var(--t-amber)]" />
              <span className="terminal-font font-bold text-[14px]">getbitsy / launchpad</span>
            </div>
          </Link>

          <div className="hidden md:flex items-center gap-4 font-mono text-[11px]">
            <span className="inline-flex items-center gap-1.5">
              <span className="t-dot-live" />
              <span className="text-[color:var(--t-emerald)]">PROTOCOL_ONLINE</span>
            </span>
            <span className="text-[color:var(--t-border-2)]">│</span>
            <LiveClock />
            {alertCount > 0 && (
              <>
                <span className="text-[color:var(--t-border-2)]">│</span>
                <span className="text-[color:var(--t-amber)] inline-flex items-center gap-1.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-[color:var(--t-amber)] animate-pulse" />
                  {alertCount} HOT
                </span>
              </>
            )}
            {warnCount > 0 && (
              <span className="text-[color:var(--t-amber)]/70">{warnCount} warn</span>
            )}
          </div>

          <div className="flex items-center gap-2 shrink-0">
            {/* Named-layouts switcher */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  data-testid="overview-layouts-trigger"
                  className="t-btn-ghost h-8 px-3 text-xs font-mono inline-flex items-center gap-1.5 max-w-[180px]"
                  title={activeLayout ? `Active · ${activeLayout.name}` : isSharedView ? `Shared · ${sharedLayoutName}` : 'No layout saved'}
                >
                  <Bookmark className="w-3 h-3" />
                  <span className="truncate">
                    {activeLayout
                      ? activeLayout.name
                      : isSharedView
                        ? `SHARED · ${sharedLayoutName}`
                        : 'LAYOUT'}
                  </span>
                  {hasUnsavedChanges && (
                    <span
                      className="w-1.5 h-1.5 rounded-full bg-[color:var(--t-amber)]"
                      title="unsaved changes"
                      data-testid="overview-layouts-unsaved-dot"
                    />
                  )}
                  <ChevronDown className="w-3 h-3 opacity-70" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="end"
                className="min-w-[240px] bg-[color:var(--t-bg)] border-[color:var(--t-border)] text-[color:var(--t-text)] font-mono"
                data-testid="overview-layouts-menu"
              >
                <DropdownMenuLabel className="font-mono text-[10px] uppercase tracking-[0.2em] text-[color:var(--t-text-3)]">
                  saved layouts
                </DropdownMenuLabel>

                {layouts.length === 0 && (
                  <div className="px-2 py-2 text-[11px] text-[color:var(--t-text-3)]" data-testid="overview-layouts-empty">
                    No saved layouts yet.
                  </div>
                )}

                {layouts.map((l) => {
                  const isActive = l.id === activeLayoutId;
                  return (
                    <div
                      key={l.id}
                      className="flex items-stretch gap-0.5"
                      data-testid={`overview-layouts-item-${l.id}`}
                    >
                      <DropdownMenuItem
                        onClick={() => handleApplyLayout(l.id)}
                        className="flex-1 cursor-pointer text-[12px] focus:bg-[color:var(--t-bg-2)] focus:text-[color:var(--t-amber)]"
                        data-testid={`overview-layouts-apply-${l.id}`}
                      >
                        {isActive ? (
                          <Check className="w-3 h-3 mr-2 text-[color:var(--t-amber)]" />
                        ) : (
                          <CircleDot className="w-3 h-3 mr-2 text-[color:var(--t-text-3)]" />
                        )}
                        <span className="truncate">{l.name}</span>
                      </DropdownMenuItem>
                      <button
                        onClick={(e) => { e.stopPropagation(); handleRename(l); }}
                        className="px-1.5 text-[color:var(--t-text-3)] hover:text-[color:var(--t-amber)]"
                        title="Rename"
                        data-testid={`overview-layouts-rename-${l.id}`}
                      >
                        <Pencil className="w-3 h-3" />
                      </button>
                      <button
                        onClick={(e) => { e.stopPropagation(); handleDelete(l); }}
                        className="px-1.5 text-[color:var(--t-text-3)] hover:text-red-400"
                        title="Delete"
                        data-testid={`overview-layouts-delete-${l.id}`}
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                  );
                })}

                <DropdownMenuSeparator className="bg-[color:var(--t-border)]" />

                {activeLayout && hasUnsavedChanges && (
                  <DropdownMenuItem
                    onClick={handleSaveChanges}
                    className="text-[12px] cursor-pointer focus:bg-[color:var(--t-bg-2)] focus:text-[color:var(--t-amber)]"
                    data-testid="overview-layouts-save-changes"
                  >
                    <Save className="w-3 h-3 mr-2 text-[color:var(--t-amber)]" />
                    Save changes to &ldquo;{activeLayout.name}&rdquo;
                  </DropdownMenuItem>
                )}

                <DropdownMenuItem
                  onClick={handleSaveAs}
                  className="text-[12px] cursor-pointer focus:bg-[color:var(--t-bg-2)] focus:text-[color:var(--t-amber)]"
                  data-testid="overview-layouts-save-as"
                >
                  <Plus className="w-3 h-3 mr-2" />
                  Save current order as…
                </DropdownMenuItem>

                <DropdownMenuItem
                  onClick={handleShare}
                  className="text-[12px] cursor-pointer focus:bg-[color:var(--t-bg-2)] focus:text-[color:var(--t-amber)]"
                  data-testid="overview-layouts-share"
                >
                  <Share2 className="w-3 h-3 mr-2" />
                  Copy share link
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            {isCustomOrder && (
              <button
                onClick={resetOrder}
                data-testid="overview-reset-order"
                className="t-btn-ghost h-8 px-3 text-xs font-mono inline-flex items-center gap-1.5"
                title="Reset to default desk order"
              >
                <RotateCcw className="w-3 h-3" />
                RESET
              </button>
            )}
            <button
              onClick={load}
              disabled={refreshing}
              data-testid="overview-refresh"
              className="t-btn-ghost h-8 px-3 text-xs font-mono inline-flex items-center gap-1.5"
            >
              <RefreshCw className={'w-3 h-3 ' + (refreshing ? 'animate-spin' : '')} />
              REFRESH
            </button>
          </div>
        </div>
      </header>

      {/* Hero strip */}
      <section className="relative border-b border-[color:var(--t-border)]">
        <div className="max-w-[1600px] mx-auto px-5 sm:px-8 lg:px-10 py-10 sm:py-14">
          <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-[color:var(--t-amber)] mb-3">
            § Morning Briefing
          </div>
          <h1 className="terminal-font text-4xl sm:text-5xl lg:text-6xl font-black tracking-[-0.03em] leading-[0.95]" data-testid="overview-title">
            The whole floor,<br />
            <span className="text-[color:var(--t-amber)]">one screen</span>.
          </h1>
          <p className="mt-5 text-[color:var(--t-text-2)] max-w-xl leading-relaxed text-[15px]">
            Six desks, four metrics each, polled every 30 seconds. Project this on the wall monitor,
            walk through each panel, and you&rsquo;ve briefed the whole protocol before coffee is done.
          </p>
          {lastFetch && (
            <div className="mt-4 font-mono text-[10px] text-[color:var(--t-text-3)] flex flex-wrap items-center gap-x-3 gap-y-1">
              <span>last update &middot; {lastFetch.toLocaleTimeString()} &middot; auto-refresh 30s</span>
              {activeLayout && !hasUnsavedChanges && (
                <span className="text-[color:var(--t-emerald)]" data-testid="overview-layout-status-saved">
                  &middot; layout · <span className="text-[color:var(--t-text)]">{activeLayout.name}</span> · saved
                </span>
              )}
              {activeLayout && hasUnsavedChanges && (
                <span className="text-[color:var(--t-amber)]" data-testid="overview-layout-status-unsaved">
                  &middot; layout · <span className="text-[color:var(--t-text)]">{activeLayout.name}</span> · unsaved changes
                </span>
              )}
              {!activeLayout && isSharedView && (
                <span className="text-[color:var(--t-amber)]" data-testid="overview-layout-status-shared">
                  &middot; viewing shared layout · <span className="text-[color:var(--t-text)]">{sharedLayoutName}</span> · save to keep
                </span>
              )}
              {!activeLayout && !isSharedView && isCustomOrder && (
                <span className="text-[color:var(--t-amber)]/80">&middot; custom layout (unsaved)</span>
              )}
            </div>
          )}
        </div>
      </section>

      {/* Launchpad grid */}
      <section className="py-10">
        <div className="max-w-[1600px] mx-auto px-5 sm:px-8 lg:px-10">
          <div className="mb-4 font-mono text-[10px] text-[color:var(--t-text-3)]">
            &uarr; drag any panel to reorder &middot; save named layouts from the LAYOUT menu &middot; share via copy-link
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-5" data-testid="overview-launchpad-grid">
            {order.map((k) => {
              const desk = DESK_BY_KEY[k];
              if (!desk) return null;
              return (
                <DeskPanel
                  key={desk.key}
                  desk={desk}
                  data={desks[desk.key]}
                  flashKey={flashKey}
                  draggedKey={draggedKey}
                  dropTargetKey={dropTargetKey}
                  onDragStart={handleDragStart}
                  onDragEnd={handleDragEnd}
                  onDragOver={handleDragOver}
                  onDrop={handleDrop}
                />
              );
            })}
          </div>
        </div>
      </section>

      <TerminalFloorNav />
    </div>
  );
}
